import { useTheme } from '@backoffice-frontend/patterns'
import { CoffeeIcon } from '@moia-dev/pace-icons'
import {
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@moia-dev/pace-web'

const MarkerContainerShape = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 28 28" {...props}>
    <path d="M25.5 11.7314C25.5 14.6883 24.7266 16.7289 23.0393 18.9232C21.6512 20.7286 19.6627 22.6177 16.9647 25.181C16.3193 25.7941 15.6333 26.4458 14.9053 27.1441C14.4084 27.6207 13.5988 27.6183 13.1043 27.1387C12.2705 26.33 11.4909 25.5838 10.7635 24.8877C8.23827 22.4709 6.34346 20.6575 4.99663 18.9194C3.29532 16.724 2.5 14.684 2.5 11.7314C2.5 5.53908 7.63799 0.5 14 0.5C20.362 0.5 25.5 5.53908 25.5 11.7314Z" />
  </svg>
)

export const PureBreakMarker = ({
  isClickable = false,
  isFaded = false,
  isSelected = false,
  locationName,
  returningVehiclesCount,
  showReturningVehiclesLabel = false,
  vehicleCount,
}: {
  isClickable?: boolean
  isFaded?: boolean
  isSelected?: boolean
  locationName: string
  returningVehiclesCount?: number
  showReturningVehiclesLabel?: boolean
  vehicleCount?: number
}) => {
  const theme = useTheme()

  const hasVehicleCount = !!vehicleCount && vehicleCount > 0
  const hasReturningVehicleCount =
    !!returningVehiclesCount && returningVehiclesCount > 0

  const styles = {
    root: {
      alignItems: 'center',
      cursor: 'default',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      position: 'relative',
      zIndex: 3,

      '&:hover': {
        ...(!isSelected && {
          '& .pin': {
            fill: theme.semantic.ColorSurfaceHovered,
          },

          '& .vehicleCountLabel': {
            background: theme.semantic.ColorActionSecondaryHovered,
          },
        }),
      },

      ...(!isSelected && {
        '&:active': {
          '& .pin': {
            fill: theme.semantic.ColorSurfacePressed,
          },

          '& .vehicleCountLabel': {
            background: theme.semantic.ColorActionSecondaryPressed,
          },
        },
      }),
    } as const,

    selected: {
      '& .vehicleCountLabel': {
        background: theme.semantic.ColorAccentDefault,
      },
      '& .icon': {
        fill: theme.semantic.ColorAccentDefault,
      },
    } as const,

    pin: {
      fill: theme.semantic.ColorSurfaceDefault,
      stroke: theme.semantic.ColorBackgroundDimmedReduced,
      height: isSelected ? 42 : 32,
      width: isSelected ? 36 : 28,
      zIndex: 1,
    } as const,

    icon: {
      left: 7,
      position: 'absolute',
      top: 5,
      width: 12,
      zIndex: 2,
      fill: theme.semantic.ColorActionSecondaryDefault,
    } as const,

    iconSelected: {
      left: 11,
      position: 'absolute',
      top: 10,
      width: 16,
      zIndex: 2,
      fill: theme.semantic.ColorAccentDefault,
    } as const,

    commonLabelStyles: {
      alignItems: 'center',
      display: 'flex',
      fontWeight: 'bold',
      fontSize: isSelected ? 18 : 12,
      height: isSelected ? 32 : 24,
      transition: 'background 0.3s ease-in-out',
      marginTop: isSelected ? -4 : -2,
      zIndex: 0,
    },

    vehicleCountLabel: {
      background: theme.semantic.ColorActionSecondaryDefault,
      borderBottomRightRadius:
        showReturningVehiclesLabel && hasReturningVehicleCount
          ? 0
          : isSelected
            ? 16
            : 12,
      borderTopRightRadius:
        showReturningVehiclesLabel && hasReturningVehicleCount
          ? 0
          : isSelected
            ? 16
            : 12,
      color: theme.semantic.ColorContentOnActionSecondaryDefault,
      marginLeft: isSelected ? -16 : -10,
      padding: `0px 8px 0px ${isSelected ? 18 : 12}px`,
    } as const,

    returningVehiclesCountLabel: {
      background: theme.semantic.ColorBackgroundAlternative,
      border: `1px solid ${theme.semantic.ColorBorderOnControlDefault}`,
      borderLeft: 'none',
      borderBottomRightRadius: isSelected ? 16 : 12,
      borderTopRightRadius: isSelected ? 16 : 12,
      marginLeft: !hasVehicleCount ? -14 : 0,
      padding: `0 ${theme.space.Space2} 0 ${!hasVehicleCount ? theme.space.Space3 : theme.space.Space1}`,
      color: theme.semantic.ColorAccentDefault,
    } as const,

    clickable: {
      cursor: 'pointer',
    } as const,

    faded: {
      opacity: 0.5,
      transition: 'opacity 0.3s ease-in-out',
    } as const,
  }

  return (
    <Tooltip placement="top" delay={0}>
      <TooltipTrigger>
        <div
          css={{
            ...styles.root,
            ...(isFaded && styles.faded),
            ...(isClickable && styles.clickable),
            ...(isSelected && styles.selected),
          }}
        >
          <MarkerContainerShape className="pin" css={styles.pin} />

          <Icon css={isSelected ? styles.iconSelected : styles.icon} size={16}>
            <CoffeeIcon
              color={isSelected ? theme.semantic.ColorAccentDefault : ''}
            />
          </Icon>

          {hasVehicleCount && (
            <span
              className="vehicleCountLabel"
              css={[styles.commonLabelStyles, styles.vehicleCountLabel]}
            >
              {vehicleCount}
            </span>
          )}

          {showReturningVehiclesLabel && hasReturningVehicleCount && (
            <span
              className="returningVehiclesCountLabel"
              css={[
                styles.commonLabelStyles,
                styles.returningVehiclesCountLabel,
              ]}
            >
              +{returningVehiclesCount}
            </span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>{locationName}</TooltipContent>
    </Tooltip>
  )
}
